import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { landingContent } from "../../i18n/nb";
import { t } from "../../i18n/t";
import { formatNumber } from "../../lib/format";
import { routes } from "../../lib/routes";
import { useNational } from "../../lib/useNational";
import { usePageTitle } from "../../lib/usePageTitle";
import { KommuneSearch } from "./KommuneSearch";

/** Live "N skoler i M kommuner" line; hidden while loading or when the file cannot be read. */
function NationalStat() {
  const { data } = useNational();
  if (!data) return null;
  const schools = data.totals?.schools ?? data.schools.length;
  const kommuner = data.totals?.kommuner ?? data.runs.length;
  return (
    <p className="landing-stat">
      {t("landing.statLine", { schools: formatNumber(schools), kommuner: formatNumber(kommuner) })}
    </p>
  );
}

function ReportLink() {
  return <a href="/eksempelrapport-larvik.pdf">{t("landing.reportLink")}</a>;
}

function ContactButtons() {
  return (
    <p className="cta-actions">
      <Link to={routes.kommuner()} className="btn btn-primary">
        {t("marketing.seeKommune")}
      </Link>
      <Link to={routes.contact()} className="btn btn-secondary">
        {t("marketing.orderReview")}
      </Link>
    </p>
  );
}

/** `/` for visitors who are not signed in: the sales page. */
export function LandingPage() {
  usePageTitle(t("landing.title"));
  const { hash } = useLocation();

  // Router links to /#slik and /#priser change the hash without a browser scroll.
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView?.();
  }, [hash]);

  return (
    <div className="page landing">
      <section className="landing-hero" aria-labelledby="landing-title">
        <div className="landing-hero-text">
          <p className="eyebrow">{t("landing.heroEyebrow")}</p>
          <h1 id="landing-title">{t("landing.heroTitle")}</h1>
          <p className="lead">{t("landing.heroLead")}</p>
          <ContactButtons />
        </div>
        <figure className="hero-figure">
          <img
            src="/eksempel-berg-skole.webp"
            width={820}
            height={657}
            alt={t("landing.heroFigAlt")}
            loading="eager"
          />
          <figcaption>
            {t("landing.heroFigCaption")} <ReportLink />
          </figcaption>
        </figure>
      </section>

      <section className="landing-search">
        <KommuneSearch />
        <NationalStat />
      </section>

      <section id="hvorfor" className="landing-section" aria-labelledby="hvorfor-h">
        <h2 id="hvorfor-h">{t("landing.whyTitle")}</h2>
        <p>{t("landing.whyOne")}</p>
        <p>{t("landing.whyTwo")}</p>
      </section>

      <section className="landing-section" aria-labelledby="features-h">
        <h2 id="features-h">{t("landing.featuresTitle")}</h2>
        <ul className="plain-list card-grid">
          {landingContent.features.map((f) => (
            <li key={f.title} className="card">
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </li>
          ))}
        </ul>
        <p className="report-line">
          {t("landing.reportSee")} <ReportLink /> {t("landing.reportOr")}{" "}
          <a href="/eksempel-tiltaksliste-larvik.xlsx">{t("landing.excelLink")}</a>
        </p>
      </section>

      <section id="slik" className="landing-section" aria-labelledby="slik-h">
        <h2 id="slik-h">{t("landing.howTitle")}</h2>
        <ol className="steps">
          {landingContent.steps.map((s) => (
            <li key={s.title}>
              <h3>{s.title}</h3>
              <p>{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="landing-section" aria-labelledby="honest-h">
        <h2 id="honest-h">{t("landing.honestTitle")}</h2>
        <ul>
          {landingContent.honest.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      </section>

      <section id="priser" className="landing-section" aria-labelledby="priser-h">
        <h2 id="priser-h">{t("landing.pricesTitle")}</h2>
        <p className="lead">{t("landing.pricesLead")}</p>
        <ul className="plain-list card-grid price-grid">
          {landingContent.prices.map((p) => (
            <li key={p.name} className="card price-card">
              <h3>{p.name}</h3>
              <p className="muted">{p.schools}</p>
              <p className="price">{p.price}</p>
            </li>
          ))}
        </ul>
        <div className="notice notice-info">
          <p>{t("landing.pilot")}</p>
        </div>
        <p>{t("landing.advisers")}</p>
        <p>
          <Link to={routes.contact()} className="btn btn-primary">
            {t("marketing.orderReview")}
          </Link>
        </p>
      </section>

      <section className="landing-section" aria-labelledby="faq-h">
        <h2 id="faq-h">{t("landing.faqTitle")}</h2>
        <div className="faq">
          {landingContent.faq.map((q) => (
            <details key={q.question}>
              <summary>{q.question}</summary>
              <p>{q.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="card cta-card landing-cta" aria-labelledby="cta-h">
        <h2 id="cta-h">{t("landing.ctaTitle")}</h2>
        <ContactButtons />
        <p>
          <a href="/nasjonal/">{t("marketing.nationalMap")}</a>
        </p>
      </section>
    </div>
  );
}
